<script>
import baseMixin from '@mixins/baseMixin'
import { mapActions, mapGetters } from 'vuex'
import dayjs from '@/services/date'
import { DisputeTypeStrings } from '@/constants/disputes/DisputeType'
import VuetifyColourClass from '@/constants/core/VuetifyColourClass'
import DateFormatToken from '@/constants/date/DateFormatToken'
import { ShortTermStatus } from '@/constants/booking/ShortTermStatus'
import { DisputePerspective } from '@/constants/disputes/DisputePerspective.js'
import Spinner from '@/components/general/spinner.vue'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import timesheetMixin from '@/mixins/timesheetMixin'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import DisputesDataTableCellKeys from '@/constants/disputes/DisputesDataTableCellKeys'
import { searchItem, highlightText } from '@/helpers/search-helpers'
import { DID_NOT_HAPPEN_DELTA_CODE } from '@/constants/disputes/DisputesDeltaCodes'
import ClientDetailsListItem from '../general/client-details-list-item.vue'
import DisputesTableBookingDateV2 from './disputes-table-booking-date-v2.vue'
import DisputesTableSimpleDeltaV2 from './disputes-table-simple-delta-v2.vue'
import DisputesTableBreaks from './disputes-table-breaks.vue'
import DisputesTableRestBreaksIndicator from './disputes-table-rest-breaks-indicator.vue'

const {
  BOOKING_ID,
  LOCATION_NAME,
  CANDIDATE_NAME,
  START_TIME_LOCAL,
  BREAK_MINUTES,
  DELTA_MINUTES,
  IS_REST_BREAK_COMPLIANT,
  CANDIDATE_HAS_SUBMITTED,
  CLIENT_HAS_SUBMITTED,
  BOOKING_DATE_TIME,
} = DisputesDataTableCellKeys

export default {
  components: {
    ClientDetailsListItem,
    Spinner,
    DisputesTableBookingDateV2,
    DisputesTableSimpleDeltaV2,
    DisputesTableBreaks,
    DisputesTableRestBreaksIndicator,
  },
  mixins: [baseMixin, timesheetMixin],
  props: {
    autocompleteSelectedModel: {
      type: Object,
      default: () => null,
    },
    statusFilters: {
      type: Array,
      default: () => [],
    },
    search: {
      type: String,
      default: '',
    },
    sortStrategy: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedTimesheets: [],
      isBulkSubmitLoading: [],
      disputesPagedList: [],
      tableOptions: {},
      disputePerspective: DisputePerspective,

      simpleSortColumns: {
        [BOOKING_ID]: 'number',
        [LOCATION_NAME]: 'string',
        [CANDIDATE_NAME]: 'string',
        [START_TIME_LOCAL]: 'date',
        [BREAK_MINUTES]: 'number',
        [DELTA_MINUTES]: 'number',
      },
      itemsPerPageOptions: [10, 15, 20, 50],
    }
  },
  computed: {
    ...mapGetters({
      isLoadingBookingDisputes: 'disputes/isLoading',
      currentUser: 'auth/currentUser',
      getLocation: 'client/getLocation',
    }),
    disputes() {
      if (
        isNullOrEmptyArray(this.statusFilters) ||
        isNullOrEmptyArray(this.disputesPagedList)
      )
        return this.disputesPagedList

      return this.disputesPagedList.filter((dispute) =>
        this.statusFilters.includes(dispute.bookingStatus)
      )
    },
    searchTable() {
      return (_value, q, item) =>
        searchItem(item, q, [
          this.disputesSearchDatePreprocessor,
          this.disputesSearchDeltaPreprocessor,
        ])
    },
    showRestBreaksColumn() {
      return Boolean(
        this.disputes.find(
          (dispute) =>
            dispute?.candidateTimesheet?.isRestBreakCompliant !== null
        )
      )
    },
    baseTableHeaders() {
      return [
        {
          text: this.$t('dispute.table.columnHeadings.bookingId'),
          align: 'start',
          value: BOOKING_ID,
        },
        {
          text: this.$t('dispute.table.columnHeadings.locationDetails'),
          value: LOCATION_NAME,
        },
        {
          text: this.$t('dispute.table.columnHeadings.candidate'),
          value: CANDIDATE_NAME,
        },
        {
          text: this.$t('dispute.table.columnHeadings.bookingTime'),
          value: BOOKING_DATE_TIME,
        },
        {
          text: this.$t('dispute.table.columnHeadings.breaks'),
          align: 'center',
          value: BREAK_MINUTES,
        },
        {
          text: this.$t('dispute.table.columnHeadings.delta'),
          align: 'center',
          value: DELTA_MINUTES,
        },
        {
          text: this.$t('dispute.table.columnHeadings.restBreaks'),
          align: 'center',
          value: IS_REST_BREAK_COMPLIANT,
          display: this.showRestBreaksColumn,
        },
        {
          text: this.$t('dispute.table.columnHeadings.candidatePenaltiesShort'),
          align: 'center',
          value: CANDIDATE_HAS_SUBMITTED,
        },
        {
          text: this.$t('dispute.table.columnHeadings.clientPenaltiesShort'),
          align: 'center',
          value: CLIENT_HAS_SUBMITTED,
        },
        {
          text: '',
          align: 'end',
          value: 'actions',
          sortable: false,
        },
      ]
    },
    tableHeaders() {
      return this.baseTableHeaders.filter(
        (header) => !Object.hasOwn(header, 'display') || header?.display
      )
    },
    hasTimesheetsSelected() {
      return this.selectedTimesheets.length > 0
    },
    filters() {
      return {
        placementStatuses: [
          ...ShortTermStatus.getAllMissingTimesheets(),
          ...ShortTermStatus.getConflictingTimesheetStatuses(),
        ],
        filter: this.autocompleteSelectedModel,
      }
    },
  },
  watch: {
    tableOptions: {
      async handler(val) {
        this.selectedTimesheets = []
        await this.loadList()
      },
      deep: true,
    },
    filters: {
      async handler() {
        this.selectedTimesheets = []
        await this.loadList()
        this.$emit('setGraphData', this.disputesPagedList)
      },
      deep: true,
    },
  },

  async created() {
    if (!isNullOrEmptyArray(this.autocompleteSelectedModel?.ids))
      await this.loadList()
  },

  methods: {
    ...mapActions({
      getBookingDisputeList: 'disputes/getDisputesV2',
      loadBookingDetails: 'booking/loadDetails',
    }),
    highlightText,
    customSort(items, sortBy, sortDesc) {
      const strategy = this.sortStrategy
      if (!strategy) return items
      return strategy.sort(items, sortBy, sortDesc)
    },

    async loadList(refresh) {
      const result = await this.getBookingDisputeList({
        params: this.filters,
      })
      if (result.isSuccess) {
        this.disputesPagedList = result.data
        // Clear selections when data is refreshed to prevent stale references
        if (refresh) {
          this.selectedTimesheets = []
        }
      }
    },
    formatDate(d, format = DateFormatToken.ll) {
      return dayjs(d).locale(this.$i18n.locale).format(format)
    },
    formatTimeString(timeString) {
      return dayjs(timeString, DateFormatToken.HHmm).format(DateFormatToken.LT)
    },
    status(disputeItem) {
      return {
        color: () => {
          const isMissing = ShortTermStatus.getAllMissingTimesheets().includes(
            disputeItem.bookingStatus
          )
          return isMissing ? VuetifyColourClass.warning : VuetifyColourClass.red
        },
        text: () => {
          const isMissing = ShortTermStatus.getAllMissingTimesheets().includes(
            disputeItem.bookingStatus
          )
          return isMissing
            ? DisputeTypeStrings.MISSING
            : DisputeTypeStrings.CONFLICT
        },
      }
    },
    async submitSelectedTimesheets() {
      this.isBulkSubmitLoading = this.selectedTimesheets.map(
        ({ bookingId }) => bookingId
      )

      const payload = this.selectedTimesheets.map((disputeItem) => ({
        bookingId: disputeItem.bookingId,
        operation: this.setOperationForBulkResolveBooking(disputeItem),
      }))

      await this.$store.dispatch('disputes/submitTimesheets', payload)

      this.isBulkSubmitLoading = []
      this.selectedTimesheets = []
      await this.loadList(true)
    },

    setOperationForBulkResolveBooking(disputeItem) {
      const {
        candidateTimesheet,
        clientTimesheet,
        isCandidateTimeEntriesPresent,
      } = disputeItem

      // Priority 1: Candidate timesheet
      if (candidateTimesheet) {
        return ResolveOperations.CANDIDATE
      }

      // Priority 2: Client timesheet
      if (clientTimesheet) {
        return ResolveOperations.CLIENT
      }

      // Priority 3 & 4: Clock-in data (complete or incomplete)
      if (isCandidateTimeEntriesPresent) {
        return ResolveOperations.TIME_ENTRY
      }

      // Priority 5: Original booking times (fallback)
      return ResolveOperations.ORIGINAL
    },

    loadBookingDetails(item) {
      this.$emit('loadBookingDetails', item)
    },
    /**
     * The table expects a function to return the item class.
     */
    getTableItemClass() {
      return 'cursor-pointer'
    },
    disputesSearchDatePreprocessor(item) {
      const bookingDate = this.formatDate(item?.startTimeLocal)
      const bookingStart = this.formatDate(
        item?.startTimeLocal,
        DateFormatToken.LT
      )
      const bookingEnd = this.formatDate(item?.endTimeLocal, DateFormatToken.LT)
      return `${bookingDate} - ${bookingStart} - ${bookingEnd}`
    },
    disputesSearchDeltaPreprocessor(item) {
      return item.deltaMinutes !== DID_NOT_HAPPEN_DELTA_CODE
        ? item.deltaMinutes
        : this.$t('dispute.bookingDetails.changes.didNotHappen')
    },
  },
}
</script>

<template>
  <div class="transparent">
    <v-data-table
      v-model="selectedTimesheets"
      :headers="tableHeaders"
      :items="disputes"
      :loading="isLoadingBookingDisputes"
      class="pt-4"
      :item-class="getTableItemClass"
      :search="search"
      :custom-filter="searchTable"
      item-key="bookingId"
      :footer-props="{
        itemsPerPageOptions,
      }"
      show-select
      selectable-key="isSelectable"
      multi-sort
      :custom-sort="customSort"
      @click:row="loadBookingDetails"
    >
      <template v-slot:item.bookingId="{ item }">
        <div
          v-if="!isBulkSubmitLoading.includes(item.bookingId)"
          class="w-fit-content"
          @click.stop
        >
          <BaseLink
            :href="returnBookingUrl(item.bookingId)"
            color="primary"
            :title="$t('general.link.viewBookingTooltip')"
            allow-insecure
            ><span
              v-sanitized-html="highlightText(item.bookingId, search)"
            ></span
          ></BaseLink>
        </div>
        <Spinner v-else />
      </template>

      <template v-slot:item.locationName="{ item }">
        <ClientDetailsListItem
          :location-id="item.locationId"
          :location-title="item.locationTitle"
          :search="search"
        />
      </template>

      <template v-slot:item.candidateName="{ item }">
        <div class="w-fit-content" @click.stop>
          <BaseLink
            :href="returnCandidateUrl(item.candidateId)"
            color="primary"
            :title="$t('general.link.viewCandidateTooltip')"
            allow-insecure
            ><span
              v-sanitized-html="highlightText(item.candidateName, search)"
            ></span
          ></BaseLink>
        </div>
      </template>

      <template v-slot:item.bookingDateTime="{ item }">
        <DisputesTableBookingDateV2 :dispute="item" :search="search" />
      </template>

      <template v-slot:item.breakMinutes="{ item }">
        <DisputesTableBreaks :dispute="item" :search="search" />
      </template>

      <template v-slot:item.deltaMinutes="{ item }">
        <div class="text-center d-flex justify-center">
          <DisputesTableSimpleDeltaV2 :dispute="item" :search="search" />
        </div>
      </template>

      <template v-slot:item.candidateHasSubmitted="{ item }">
        <div class="text-center d-flex justify-center">
          {{ item.candidatePenaltyDisplayValue }}
        </div>
      </template>
      <template v-slot:item.clientHasSubmitted="{ item }">
        <div class="text-center d-flex justify-center">
          {{ item.clientPenaltyDisplayValue }}
        </div>
      </template>

      <template v-slot:item.isRestBreakCompliant="{ item }">
        <div class="text-center d-flex justify-center">
          <DisputesTableRestBreaksIndicator
            v-if="
              item.hasCandidateTimesheet &&
              item.candidateTimesheet.isRestBreakCompliant !== null
            "
            :compliant="item.candidateTimesheet.isRestBreakCompliant"
          />
          <span v-else>{{ $t('general.emptyCell') }}</span>
        </div>
      </template>

      <template v-slot:item.actions>
        <div class="text-center">
          <v-icon size="20" class="opacity-7">mdi-chevron-right</v-icon>
        </div>
      </template>
      <template v-slot:footer.prepend>
        <v-btn
          color="primary"
          class="ma-2 px-8"
          depressed
          :disabled="!hasTimesheetsSelected"
          :loading="isLoadingBookingDisputes"
          @click="submitSelectedTimesheets"
        >
          {{ $t('dispute.submitLabels.latest') }}
        </v-btn>
        <p
          :key="selectedTimesheets.length"
          class="mb-0 ml-2 text-body-1 font-weight-light"
          >{{
            $t('dispute.table.selectedText', [selectedTimesheets.length])
          }}</p
        >
      </template>
    </v-data-table>
  </div>
</template>
